import { useEffect, useState } from 'react';
import { patientPortal } from '@/api/patientPortal';
import PatientPortalPageHero from './PatientPortalPageHero';
import { usePatientPortalBranding } from './PatientPortalBrandingProvider';

type ReferralRow = {
  id: string;
  patientName: string;
  phone: string;
  status: string;
  notes: string | null;
  createdAt: string;
};

const emptyReferral = {
  patientName: '',
  phone: '',
  gender: 'MALE',
  dob: '',
  email: '',
  location: '',
  alternatePhone: '',
  remarks: '',
};

export default function PatientPortalReferrals() {
  const branding = usePatientPortalBranding();
  const facility = branding.hospitalName ?? 'your hospital';
  const [referrals, setReferrals] = useState<ReferralRow[]>([]);
  const [form, setForm] = useState(emptyReferral);
  const [msg, setMsg] = useState('');

  const load = () => {
    patientPortal.getReferrals().then(setReferrals).catch(() => {});
  };

  useEffect(() => {
    load();
  }, []);

  const submit = async () => {
    setMsg('');
    try {
      await patientPortal.submitReferral(form);
      setMsg('Referral sent to hospital CRM. Our team will follow up.');
      setForm(emptyReferral);
      load();
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } } };
      setMsg(err.response?.data?.message ?? 'Could not submit referral');
    }
  };

  const field = (
    label: string,
    key: keyof typeof emptyReferral,
    type: string = 'text',
    required = false
  ) => (
    <label className="block text-sm">
      {label}
      {required ? ' *' : ''}
      <input
        type={type}
        className="mt-1 w-full border rounded px-3 py-2 text-sm"
        value={form[key]}
        onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))}
        required={required}
      />
    </label>
  );

  return (
    <div className="space-y-8 max-w-xl">
      <div>
        <PatientPortalPageHero
          imageKey="referrals"
          eyebrow="Share & earn"
          title="Referrals"
          subtitle={`Introduce friends and family to ${facility} — our team will follow up with care`}
          compact
        />
        <p className="text-sm text-muted-foreground mt-1">
          Refer friends and family to {facility}. Each submission creates a CRM lead with source{' '}
          <strong>Referral</strong> so our sales team can follow up using your remarks.
        </p>
      </div>

      <div className="border rounded-lg bg-white p-4 space-y-3">
        <h2 className="font-semibold">New referral</h2>
        {field("Person's name", 'patientName', 'text', true)}
        {field('Their mobile', 'phone', 'tel', true)}
        {field('Date of birth', 'dob', 'date')}
        <label className="block text-sm">
          Gender
          <select
            className="mt-1 w-full border rounded px-3 py-2 text-sm"
            value={form.gender}
            onChange={(e) => setForm((f) => ({ ...f, gender: e.target.value }))}
          >
            <option value="MALE">Male</option>
            <option value="FEMALE">Female</option>
            <option value="OTHER">Other</option>
          </select>
        </label>
        {field('Email', 'email', 'email')}
        {field('City / locality', 'location')}
        {field('Alternate phone', 'alternatePhone', 'tel')}
        <label className="block text-sm">
          Remarks *
          <textarea
            className="mt-1 w-full border rounded px-3 py-2 text-sm min-h-[80px]"
            placeholder="Why are you referring them? Symptoms, doctor preference, etc."
            value={form.remarks}
            onChange={(e) => setForm((f) => ({ ...f, remarks: e.target.value }))}
            required
          />
        </label>
        <button
          type="button"
          onClick={submit}
          className="w-full bg-teal-600 text-white rounded-lg py-2 font-medium"
        >
          Submit referral to CRM
        </button>
        {msg ? <p className="text-sm text-slate-600">{msg}</p> : null}
      </div>

      <div>
        <h2 className="font-semibold mb-2">Your referrals</h2>
        <ul className="border rounded-lg bg-white divide-y text-sm">
          {referrals.length === 0 ? (
            <li className="p-4 text-muted-foreground">No referrals submitted yet</li>
          ) : (
            referrals.map((r) => (
              <li key={r.id} className="p-3">
                <p className="font-medium">
                  {r.patientName} · {r.phone}
                </p>
                <p className="text-muted-foreground text-xs mt-1">
                  {r.status} · {new Date(r.createdAt).toLocaleDateString()}
                </p>
                {r.notes ? <p className="text-xs mt-1 text-slate-600">{r.notes}</p> : null}
              </li>
            ))
          )}
        </ul>
      </div>
    </div>
  );
}
